'use client'
import * as React from 'react'
import { createStore, useStore } from 'zustand'
import { persist } from 'zustand/middleware'
import { tabContentWrapperId } from '../../../tab-content/wrapper'
import { findWithIndex } from '~/lib/find-with-index'
import { Block, BlockChange, Commit } from '@basehub/data/replicache/index'
import { TreeManager } from '@basehub/utils/tree/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useUserStore } from '~/app/_store/user-store'
import { basehub } from 'basehub'
import { run } from '@basehub/utils'
import { env } from '~/env.js'
import { queryClient } from '~/lib/react-query-client'
import { max } from 'date-fns'
import { FloatingBanner, FloatingBannerFragment } from './floating-banner/root'
import { useSetLastChangelogSeen } from '../../../tab-content/changelog/use-last-changelog-seen'
import { SetOptional } from 'type-fest'
import { highlightedChangelogFilterFragment } from '../../../tab-content/changelog/fragments'

export type Tab = {
  key: string
  scrollTop?: number
  partiallyOpen: boolean
}

export type TabsStoreData = {
  activeTab: Tab | null
  tabs: Tab[]
  addTab: (
    tab: Tab,
    options?: Partial<{
      shouldResetFollowStatus: boolean
      position: 'last' | 'next-to-active' | 'first'
    }>
  ) => void
  removeTab: (tabKeys: string[]) => void
  selectTab: (tab: Tab | null, forceActiveTabOntoURL?: boolean) => void
  selectPrevTab: () => void
  selectNextTab: () => void
  selectBlockChangeForCommitTab: ({
    blockChangeId,
  }: {
    blockChangeId: BlockChange['id']
  }) => void
  selectBlockChangeForSuggestionTab: ({
    blockChangeId,
  }: {
    blockChangeId: BlockChange['id']
  }) => void
  forceActiveTabOntoURL: boolean
  markTabAsPermanent: (tabKey: string) => void
  removeAllDiffTabs: () => void
  revalidateTabsScrollPosition: () => void
  lastOpenedHistoryMap: Map<string, number> // we'll use a timestamp for easier sorting
  floatingBanner?: {
    state: 'open' | 'closed'
    data: FloatingBannerFragment
    entryId: string
  }
}

export type ParsedTab = {
  key: string
  partiallyOpen: boolean
} & (
  | { type: 'block'; blockId: Block['id']; edgeId?: string }
  | { type: 'block:diff'; blockId: Block['id']; edgeId?: string }
  | {
      type: 'commit'
      commitId: Commit['id']
      blockChangeId?: BlockChange['id']
      blockChangeEdgeId?: string
    }
  | {
      type: 'merge'
      crId: string
      blockId: string
      edgeId?: string
    }
  | { type: 'changelog'; entryId: string; version: string | null }
  | { type: 'branches'; tab: string | undefined }
  | ({ type: 'dev' } & (
      | { tab: 'connect'; label: 'Connect to Your App' }
      | { tab: 'api-explorer'; label: 'API Explorer' }
    ))
  | ({ type: 'settings' } & (
      | { tab: 'general'; label: 'General' }
      | { tab: 'public-info'; label: 'Public Info' | 'README' }
    ))
  | ({ type: 'agents' } & { tab: 'settings'; label: 'Settings for Agents' })
  | { type: 'timeline' }
  | { type: 'site'; siteId: string; href?: string }
  | {
      type: 'suggestion'
      branchId: string
      blockChangeId?: string
    }
)

const getScrollerScrollTop = () => {
  const tabContentWrapper = document.getElementById(tabContentWrapperId)
  if (!tabContentWrapper?.childNodes.length) return 0

  // This is a bit hacky and can be easily broken by adding htmlelements in the tabcontentdefaultwrapper, we should seek for a workaround soon enough
  const currentTabScrollTop =
    (tabContentWrapper.childNodes[1] as HTMLDivElement)?.scrollTop ?? 0

  return currentTabScrollTop
}

export const checkTabExists = (t: Tab, tab: Tab) => {
  if (t.key === tab.key) return true
  const parsedExisting = parseTab(t)
  const parsedNewTab = parseTab(tab)

  if (
    parsedExisting.type === 'branches' &&
    parsedNewTab.type === parsedExisting.type
  ) {
    return true
  }

  if (
    (parsedExisting.type === 'settings' ||
      parsedExisting.type === 'dev' ||
      parsedExisting.type === 'agents') &&
    parsedNewTab.type === parsedExisting.type
  ) {
    return parsedNewTab.tab === parsedExisting.tab
  }

  if (parsedExisting.type === 'commit' && parsedNewTab.type === 'commit') {
    return parsedExisting.commitId === parsedNewTab.commitId
  }

  if (
    parsedExisting.type === 'changelog' &&
    parsedNewTab.type === 'changelog'
  ) {
    return parsedExisting.entryId === parsedNewTab.entryId
  }

  if (parsedExisting.type === 'site' && parsedNewTab.type === 'site') {
    return parsedExisting.siteId === parsedNewTab.siteId
  }

  if (
    parsedExisting.type === 'suggestion' &&
    parsedNewTab.type === 'suggestion'
  ) {
    return parsedExisting.branchId === parsedNewTab.branchId
  }

  return false
}

export const getTabsScrollTop = (tabs: Tab[], activeTab: Tab | null) => {
  if (!activeTab) return tabs

  const currentTabScrollTop = getScrollerScrollTop()

  return tabs.map((tab) => {
    if (tab.key !== activeTab.key) return tab

    return {
      ...tab,
      scrollTop: currentTabScrollTop,
    }
  })
}

export function parseTab<
  CastAs extends undefined | ParsedTab['type'] = undefined,
>(
  tab: Tab,
  opts?: {
    treeManager?: TreeManager<Block, NonNullable<unknown>>
    castAs?: CastAs
  }
): CastAs extends ParsedTab['type']
  ? Extract<ParsedTab, { type: CastAs }> | null
  : ParsedTab {
  const [first, second, third, fourth] = tab.key.split(':')

  const timelineTab = {
    key: tab.key,
    partiallyOpen: tab.partiallyOpen,
    type: 'timeline',
  } satisfies ParsedTab

  let parsedTab: ParsedTab = timelineTab

  if (first && first !== 'timeline') {
    if (first === 'diff' && second) {
      const [blockId, edgeId] = second.split('#') as [
        string,
        string | undefined,
      ]

      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        type: 'block:diff',
        blockId: blockId,
        edgeId,
      }
    } else if (first === 'commit' && second) {
      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        type: 'commit',
        commitId: second,
        blockChangeId: third,
      }
    } else if (first === 'suggestion' && second) {
      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        type: 'suggestion',
        branchId: second,
        blockChangeId: third,
      }
    } else if (first === 'merge' && third) {
      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        type: 'merge',
        crId: second ?? '',
        blockId: third,
        edgeId: fourth,
      }
    } else if (first === 'changelog') {
      const { version } = (queryClient.getQueryData([first, second]) as
        | { version: string }
        | undefined
        | null) ?? { version: null }

      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        entryId: second || '',
        type: 'changelog',
        version,
      }
    } else if (first === 'branches') {
      parsedTab = {
        key: tab.key,
        partiallyOpen: false,
        type: 'branches',
        tab: second,
      }
    } else if (
      first === 'dev' &&
      second &&
      ['connect', 'api-explorer'].includes(second)
    ) {
      parsedTab =
        second === 'connect'
          ? {
              key: tab.key,
              partiallyOpen: false,
              label: 'Connect to Your App',
              tab: 'connect',
              type: 'dev',
            }
          : {
              key: tab.key,
              partiallyOpen: false,
              label: 'API Explorer',
              tab: 'api-explorer',
              type: 'dev',
            }
    } else if (
      first === 'settings' &&
      second &&
      ['general', 'public-info'].includes(second)
    ) {
      parsedTab =
        second === 'general'
          ? {
              key: tab.key,
              partiallyOpen: false,
              label: 'General',
              tab: 'general',
              type: 'settings',
            }
          : {
              key: tab.key,
              partiallyOpen: false,
              label: 'Public Info',
              tab: 'public-info',
              type: 'settings',
            }
    } else if (first === 'agents' && second && ['settings'].includes(second)) {
      parsedTab = {
        key: tab.key,
        partiallyOpen: false,
        label: 'Settings for Agents',
        tab: 'settings',
        type: 'agents',
      }
    } else if (first === 'site' && second) {
      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        type: 'site',
        siteId: second,
        href: third,
      }
    } else if (first) {
      const [blockId, edgeId] = first.split('#') as [string, string | undefined]
      const node = opts?.treeManager?.getNode(blockId, {
        fallbackKey: 'sourceBlockId',
      })
      parsedTab = {
        key: tab.key,
        partiallyOpen: tab.partiallyOpen,
        type: 'block',
        blockId: node?.value.id ?? blockId,
        edgeId,
      }
    }
  }

  if (opts?.castAs && parsedTab.type !== opts.castAs) {
    return null as CastAs extends ParsedTab['type']
      ? Extract<ParsedTab, { type: CastAs }> | null
      : ParsedTab
  }

  return parsedTab as CastAs extends ParsedTab['type']
    ? Extract<ParsedTab, { type: CastAs }> | null
    : ParsedTab
}

export function getTabKey(
  data: SetOptional<ParsedTab, 'key' | 'partiallyOpen'>,
  opts?: {
    promoteEdgeToMain?: boolean
  }
): string {
  switch (data.type) {
    case 'block':
      if (opts?.promoteEdgeToMain && data.edgeId) {
        return data.edgeId
      }
      return data.blockId + (data.edgeId ? `#${data.edgeId}` : '')
    case 'block:diff':
      if (opts?.promoteEdgeToMain && data.edgeId) {
        return data.edgeId
      }
      return 'diff:' + data.blockId + (data.edgeId ? `#${data.edgeId}` : '')
    case 'suggestion':
      return `suggestion:${data.branchId}${
        data.blockChangeId ? `:${data.blockChangeId}` : ''
      }`
    case 'agents':
      return `agents:${data.tab}`
    default:
      throw new Error(`tab type ${data.type} not implemented. please do it!`)
  }
}

export function parseTabUrl(url: string): ParsedTab {
  const key = url
  const parsed = parseTab({ key, scrollTop: 0, partiallyOpen: false })
  return parsed
}

const createTabsStore = (
  repoId: string,
  {
    blocksTreeManagerRef,
  }: {
    blocksTreeManagerRef: React.MutableRefObject<TreeManager<
      Block,
      NonNullable<unknown>
    > | null>
  }
) => {
  return createStore<TabsStoreData>()(
    persist(
      (set, get) => {
        const removeTab = (tabKeys: string[]) => {
          const currentState = get()
          const clone = [...tabKeys]

          clone.push(
            // also add its sourced key if it's a block
            ...tabKeys.map((k) => {
              try {
                return blocksTreeManagerRef.current
                  ? idToSourceBlockId(k, blocksTreeManagerRef.current)
                  : k
              } catch (err) {
                return k
              }
            })
          )

          let newActiveTab = currentState.activeTab
          const newTabs = currentState.tabs.filter(
            (t) => !clone.includes(t.key)
          )

          const activeTabThatIsBeingClosed = findWithIndex(
            currentState.tabs,
            (t) =>
              clone.includes(t.key) && t.key === currentState.activeTab?.key
          )

          if (activeTabThatIsBeingClosed) {
            const nextTab = currentState.tabs.find((t, i) => {
              return (
                i > activeTabThatIsBeingClosed.index &&
                !clone.includes(t.key) &&
                t.key !== 'timeline'
              )
            })
            if (nextTab) {
              // found next tab!
              newActiveTab = nextTab
            } else {
              // else fetch for previous tab
              const previousTab = currentState.tabs.findLast((t, i) => {
                return (
                  i < activeTabThatIsBeingClosed.index &&
                  !clone.includes(t.key) &&
                  t.key !== 'timeline'
                )
              })
              if (previousTab) {
                newActiveTab = previousTab
              } else {
                // else there are no tabs left
                newActiveTab = null
              }
            }
          }

          set({
            tabs: newTabs,
            activeTab: newActiveTab,
            forceActiveTabOntoURL: true,
          })
        }

        const selectTab = (
          tab: Tab | null,
          forceActiveTabOntoURL?: boolean
        ) => {
          set((state) => {
            const lastOpenedHistoryMap = new Map(state.lastOpenedHistoryMap)

            if (tab) {
              lastOpenedHistoryMap.set(tab.key, Date.now())
              useUserStore.setState({ followingUserId: null })
            }

            return {
              tabs: getTabsScrollTop(state.tabs, state.activeTab),
              activeTab: tab,
              ...(forceActiveTabOntoURL !== undefined
                ? { forceActiveTabOntoURL }
                : {}),
              lastOpenedHistoryMap,
            }
          })
        }

        return {
          activeTab: null,
          tabs: [],
          lastOpenedHistoryMap: new Map(),
          addTab: (tab, options) => {
            const definedOptions = {
              shouldResetFollowStatus: true,
              position: 'next-to-active',
              ...options,
            } satisfies Parameters<TabsStoreData['addTab']>['1']

            // todo implement _position

            const parsedTab = parseTab(tab)
            if (parsedTab.type === 'block' && blocksTreeManagerRef.current) {
              try {
                const sourcedKey = idToSourceBlockId(
                  parsedTab.blockId,
                  blocksTreeManagerRef.current
                )
                tab.key = sourcedKey
              } catch (err) {
                // ignore. maybe the block isn't yet in the tree manager (example: block has just been created)
              }
              if (parsedTab.edgeId) {
                try {
                  const sourcedKey = idToSourceBlockId(
                    parsedTab.edgeId,
                    blocksTreeManagerRef.current
                  )
                  tab.key = `${tab.key}#${sourcedKey}`
                } catch (err) {
                  // ignore. maybe the block isn't yet in the tree manager (example: block has just been created)
                }
              }
            }

            set((state) => {
              const alreadyExistingTabIndex = state.tabs.findIndex((t) =>
                checkTabExists(t, tab)
              )
              const alreadyExistingTab = state.tabs[alreadyExistingTabIndex]
              if (
                alreadyExistingTab &&
                (parseTab(alreadyExistingTab).type === 'settings' ||
                  parseTab(alreadyExistingTab).type === 'dev' ||
                  parseTab(alreadyExistingTab).type === 'agents' ||
                  parseTab(alreadyExistingTab).type === 'branches' ||
                  parseTab(alreadyExistingTab).type === 'commit')
              ) {
                const newTabs = [...state.tabs]
                newTabs[alreadyExistingTabIndex] = {
                  ...alreadyExistingTab,
                  key: tab.key,
                }
                const activeTab = newTabs[alreadyExistingTabIndex]

                return {
                  ...state,
                  tabs: newTabs,
                  activeTab,
                  forceActiveTabOntoURL: true,
                }
              }

              const tabsWithTheActiveTabScrollPositionSet = getTabsScrollTop(
                state.tabs,
                state.activeTab
              )

              const filtered =
                // if the new tab is already open and is not partially open,
                // then i don't need to filter the partiallyOpen tabs away!
                alreadyExistingTab && !alreadyExistingTab.partiallyOpen
                  ? tabsWithTheActiveTabScrollPositionSet
                  : // only when opening a new tab do i need to filter the current partially opened tab
                    // so that it opens in replacement to the partially opened one.
                    // this mimics vscode's behaviour.
                    tabsWithTheActiveTabScrollPositionSet.filter((t) => {
                      return !t.partiallyOpen
                    })

              const tabFromListWithScrollTop = filtered.find((t) =>
                checkTabExists(t, tab)
              )

              const lastOpenedHistoryMap = new Map(state.lastOpenedHistoryMap)

              if (tab) {
                lastOpenedHistoryMap.set(tab.key, Date.now())

                definedOptions.shouldResetFollowStatus &&
                  useUserStore.setState({ followingUserId: null })
              }

              return {
                tabs: tabFromListWithScrollTop ? filtered : [...filtered, tab],
                activeTab: tabFromListWithScrollTop ?? tab,
                forceActiveTabOntoURL: true,
                lastOpenedHistoryMap,
              }
            })
          },
          markTabAsPermanent: (tabKey) => {
            set((state) => {
              return {
                tabs: state.tabs.map((t) => {
                  if (t.key === tabKey) {
                    return { ...t, partiallyOpen: false }
                  } else {
                    return t
                  }
                }),
                activeTab:
                  state.activeTab?.key === tabKey
                    ? { ...state.activeTab, partiallyOpen: false }
                    : state.activeTab,
              }
            })
          },
          removeTab,
          selectTab,
          forceActiveTabOntoURL: false,
          selectPrevTab: () => {
            const currentState = get()

            const sorted = [...currentState.tabs].sort((a) => {
              return a.key === 'timeline' ? -1 : 0
            })

            const indexOfTab = sorted.findIndex(
              (t) => t.key === currentState.activeTab?.key
            )

            const newActiveTab =
              indexOfTab === 0
                ? sorted[sorted.length - 1]
                : sorted[indexOfTab - 1]
            selectTab(newActiveTab ?? null, true)
          },
          selectBlockChangeForCommitTab: ({
            blockChangeId,
          }: {
            blockChangeId: BlockChange['id']
          }) => {
            const currentState = get()

            if (!currentState.activeTab) return
            if (parseTab(currentState.activeTab).type !== 'commit') return

            set((state) => {
              if (!currentState.activeTab) return state

              const [first, second] = currentState.activeTab.key.split(':')

              let newActiveTab = currentState.activeTab
              const newTabs = state.tabs.map((t) => {
                if (!currentState.activeTab) return t
                const parsed = parseTab(t)
                if (parsed.type !== 'commit') return t
                if (parsed.commitId !== second) return t
                newActiveTab = {
                  ...t,
                  partiallyOpen: false,
                  key: [first, second, blockChangeId].join(':'),
                }
                return newActiveTab
              })

              return {
                tabs: newTabs,
                activeTab: newActiveTab,
                forceActiveTabOntoURL: true,
              }
            })
          },
          selectBlockChangeForSuggestionTab: ({
            blockChangeId,
          }: {
            blockChangeId: BlockChange['id']
          }) => {
            const currentState = get()

            if (!currentState.activeTab) return
            if (parseTab(currentState.activeTab).type !== 'suggestion') return

            set((state) => {
              if (!currentState.activeTab) return state

              const [first, second] = currentState.activeTab.key.split(':')

              let newActiveTab = currentState.activeTab
              const newTabs = state.tabs.map((t) => {
                if (!currentState.activeTab) return t
                const parsed = parseTab(t)
                if (parsed.type !== 'suggestion') return t
                if (parsed.branchId !== second) return t
                newActiveTab = {
                  ...t,
                  partiallyOpen: false,
                  key: [first, second, blockChangeId].join(':'),
                }
                return newActiveTab
              })

              return {
                tabs: newTabs,
                activeTab: newActiveTab,
                forceActiveTabOntoURL: true,
              }
            })
          },
          selectNextTab: () => {
            const currentState = get()

            const sorted = [...currentState.tabs].sort((a) => {
              return a.key === 'timeline' ? -1 : 0
            })

            const indexOfTab = sorted.findIndex(
              (t) => t.key === currentState.activeTab?.key
            )

            const newActiveTab =
              indexOfTab === sorted.length - 1
                ? sorted[0]
                : sorted[indexOfTab + 1]
            selectTab(newActiveTab ?? null, true)
          },
          removeAllDiffTabs: () => {
            const diffTabs = get().tabs.filter(
              (t) => parseTab(t).type === 'block:diff'
            )
            removeTab(diffTabs.map((t) => t.key))
          },
          revalidateTabsScrollPosition: () => {
            set((state) => {
              const tabsWithTheActiveTabScrollPositionSet = getTabsScrollTop(
                state.tabs,
                state.activeTab
              )
              return {
                tabs: tabsWithTheActiveTabScrollPositionSet,
                activeTab: tabsWithTheActiveTabScrollPositionSet.find(
                  (t) => t.key === state.activeTab?.key
                ),
              }
            })
          },
        }
      },
      {
        name: `bshb-tabs-${repoId}`,
        storage: {
          getItem: (name) => {
            const str = sessionStorage.getItem(name)
            if (!str) return null
            const { state } = JSON.parse(str) as {
              state: SetOptional<TabsStoreData, 'lastOpenedHistoryMap'>
            }
            return {
              state: {
                ...state,
                lastOpenedHistoryMap: new Map(state.lastOpenedHistoryMap),
              } satisfies TabsStoreData,
            }
          },
          setItem: (name, newValue: { state: TabsStoreData }) => {
            // functions cannot be JSON encoded
            const str = JSON.stringify({
              state: {
                ...newValue.state,
                lastOpenedHistoryMap: Array.from(
                  newValue.state.lastOpenedHistoryMap.entries()
                )
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 12),
              },
            })
            sessionStorage.setItem(name, str)
          },
          removeItem: (name) => sessionStorage.removeItem(name),
        },
      }
    )
  )
}

export type TabsStore = ReturnType<typeof createTabsStore>

const TabsStoreContext = React.createContext<TabsStore | null>(null)

export const TabsStoreProvider = ({
  children,
  repoId,
}: {
  children: React.ReactNode
  repoId: string
}) => {
  const user = useUserStore((s) => (s.isSyncing ? null : s.optimisticUser))
  const blocksTreeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  // eslint-disable-next-line react-compiler/react-compiler
  const store = React.useRef(
    createTabsStore(repoId, { blocksTreeManagerRef })
  ).current

  const floatingBanner = useStore(store, (s) => s.floatingBanner)

  const setLastChangelogSeen = useSetLastChangelogSeen()

  React.useEffect(() => {
    if (!user?.lastChangelogSeen) return
    if (user.showReleaseNote === false) return
    const currentState = store.getState()
    const lastChangelogSeenLS = localStorage.getItem('changelog-seen')
    const lastChangelogSeenUser = user.lastChangelogSeen

    run(async () => {
      const {
        changelog: {
          entries: {
            items: [mostRecentEntry],
          },
        },
      } = await basehub({ token: env.NEXT_PUBLIC_BASEHUB_TOKEN }).query({
        changelog: {
          entries: {
            __args: {
              first: 1,
              orderBy: 'publishDate__DESC',
              filter: highlightedChangelogFilterFragment,
            },
            items: {
              publishDate: true,
              _id: true,
              highlight: {
                __typename: true,
                on_AsFloatingBannerComponent: FloatingBannerFragment,
                on_AsNewTabComponent: { value: true },
              },
            },
          },
        },
      })

      if (!mostRecentEntry) return
      if (
        [lastChangelogSeenLS, lastChangelogSeenUser].includes(
          mostRecentEntry._id
        )
      ) {
        return
      }

      if (
        (lastChangelogSeenLS && isValidISODateString(lastChangelogSeenLS)) ||
        (lastChangelogSeenUser && isValidISODateString(lastChangelogSeenUser))
      ) {
        const lastChangelogSeenMaxDate = max([
          lastChangelogSeenLS && isValidISODateString(lastChangelogSeenLS)
            ? new Date(lastChangelogSeenLS)
            : new Date(0),
          lastChangelogSeenUser && isValidISODateString(lastChangelogSeenUser)
            ? new Date(lastChangelogSeenUser)
            : new Date(0),
        ])
        // backwards compat to when we used to store the changelog date
        if (
          lastChangelogSeenMaxDate.toISOString() > mostRecentEntry.publishDate
        ) {
          return
        }
      }

      if (mostRecentEntry.highlight?.__typename === 'AsNewTabComponent') {
        // then we'll open in a new tab
        currentState.addTab({
          key: 'changelog:' + mostRecentEntry._id,
          partiallyOpen: false,
        })
      } else if (
        mostRecentEntry.highlight?.__typename === 'AsFloatingBannerComponent'
      ) {
        // then we'll open in a popover
        store.setState({
          floatingBanner: {
            state: 'open',
            data: mostRecentEntry.highlight,
            entryId: mostRecentEntry._id,
          },
        })
      }
    })
  }, [store, user?.lastChangelogSeen, user?.showReleaseNote])

  return (
    <TabsStoreContext.Provider value={store}>
      {children}
      {floatingBanner && (
        <FloatingBanner
          {...floatingBanner}
          onClose={() => {
            setLastChangelogSeen(floatingBanner.entryId)
            store.setState({ floatingBanner: undefined })
          }}
        />
      )}
    </TabsStoreContext.Provider>
  )
}

export function useTabsStoreRoot() {
  const store = React.useContext(TabsStoreContext)
  if (!store) throw new Error('Missing TabsStoreContext.Provider in the tree')
  return store
}

export function useTabsStore<T>(selector: (state: TabsStoreData) => T): T {
  const store = useTabsStoreRoot()
  return useStore(store, selector)
}

type TabPrefix = '' | `commit:${string}` | `merge:${string}` | 'diff:'
export const useGetTabPrefix = (opts?: {
  defaultValue: TabPrefix
}): TabPrefix => {
  const activeTab = useTabsStore((s) => s.activeTab)
  const parsedActiveTab = activeTab ? parseTab(activeTab) : undefined
  const activeTabType = activeTab ? parsedActiveTab?.type : ''
  const crId = parsedActiveTab?.type === 'merge' ? parsedActiveTab.crId : null
  const commitId =
    parsedActiveTab?.type === 'commit' ? parsedActiveTab.commitId : null

  let tabPrefix: TabPrefix = opts?.defaultValue ?? ''
  if (activeTabType === 'merge' && crId) {
    tabPrefix = `merge:${crId}:`
  } else if (activeTabType === 'commit' && commitId) {
    tabPrefix = `commit:${commitId}:`
  } else if (activeTabType === 'block:diff') {
    tabPrefix = 'diff:'
  }

  return tabPrefix
}

function isValidISODateString(d: string) {
  try {
    return new Date(d).toISOString() === d
  } catch (err) {
    return false
  }
}
